import React from 'react';
import { motion } from 'framer-motion';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';

const useStyles = createUseStyles({
	howItWorks: {
		paddingTop: '6rem',
		paddingBottom: '6rem',
	},
	card: {
		height: '100%',
		padding: '3rem 2.5rem',
		borderRadius: '1rem',
		border: '1px solid rgba(0, 0, 0, 0.08)',
		backgroundColor: '#fff',
		transform: 'translateY(0)',
		boxShadow: 'none',
		transition: 'transform 0.2s ease, box-shadow 0.2s ease',
		'&:hover': {
			transform: 'translateY(-4px)',
			boxShadow: '0 12px 32px rgba(0, 0, 0, 0.08)',
		},
	},
	badge: {
		width: '3.5rem',
		height: '3.5rem',
		borderRadius: '50%',
		backgroundColor: 'rgba(13, 202, 240, 0.12)',
		color: '#0a3a4a',
		fontWeight: 700,
		fontSize: '1.5rem',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginBottom: '1.5rem',
	},
	stepTitle: {
		fontWeight: 600,
		fontSize: '1.75rem',
		marginBottom: '0.75rem',
	},
	stepBody: {
		fontSize: '1.125rem',
		color: 'var(--bs-gray-700)',
		lineHeight: 1.65,
		marginBottom: 0,
	},
});

const STEPS = [
	{
		id: 'data',
		title: 'Bring your data',
		body:
			"Upload a CSV, a folder of images, or a set of documents — or start from our public-data catalog if you don't have data yet.",
	},
	{
		id: 'train',
		title: 'Pick a model recipe and train',
		body:
			'Choose a recipe that matches your task. We handle the training infrastructure, evaluation, and hyperparameters for you.',
	},
	{
		id: 'ship',
		title: 'Ship via the hosted endpoint',
		body:
			'Every trained model gets a live HTTPS endpoint with an API key. Call it from any app, or download the artifact and self-host.',
	},
];

const containerVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const itemVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

const SectionHowItWorks = () => {
	const classes = useStyles();
	return (
		<div id='how-it-works' className={classNames('row scroll-margin', classes.howItWorks)}>
			<div className='col-12 text-center mb-3'>
				<div className='display-4 fw-bold'>How it works</div>
			</div>
			<div className='col-12 text-center mb-5'>
				<p className='lead text-muted mb-0'>
					Three steps from raw data to a live model your app can call.
				</p>
			</div>
			<div className='col-lg-10 mx-auto'>
				<motion.div
					className='row g-4'
					variants={containerVariants}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.1 }}>
					{STEPS.map((step, index) => (
						<motion.div
							key={step.id}
							className='col-md-4'
							variants={itemVariants}>
							<div className={classes.card}>
								<div className={classes.badge}>{index + 1}</div>
								<div className={classes.stepTitle}>{step.title}</div>
								<p className={classes.stepBody}>{step.body}</p>
							</div>
						</motion.div>
					))}
				</motion.div>
			</div>
		</div>
	);
};

export default SectionHowItWorks;
